import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, TextField, Button, Card, CardContent, Grid2 as Grid, MenuItem,
  Alert, CircularProgress, Chip, Paper, Table, TableHead, TableBody, TableRow,
  TableCell, TablePagination, InputAdornment,
} from '@mui/material';
import { Refresh, CreditCard, Search } from '@mui/icons-material';
import { paymentsService, type TokenPayment, type TokenPaymentsResponse } from '../../../services';

const cve = (v: number) => `${Math.round(v || 0).toLocaleString('pt-PT')}$`;

const ESTADOS: Record<string, { label: string; color: 'success' | 'error' | 'warning' | 'info' | 'default' }> = {
  PAID: { label: 'Pago', color: 'success' },
  FAILED: { label: 'Falhou', color: 'error' },
  PENDING: { label: 'Pendente', color: 'warning' },
  REFUNDED: { label: 'Devolvido', color: 'info' },
};

// transactionCode do pedido à SISP: 2 = serviço, 3 = recarga, 6 = compra.
const TIPOS_TX: Record<string, string> = { '2': 'Serviço', '3': 'Recarga', '6': 'Compra' };

const dataCV = (v: string) =>
  new Date(v).toLocaleString('pt-PT', { timeZone: 'Atlantic/Cape_Verde', dateStyle: 'short', timeStyle: 'medium' });

const TokenPayments: React.FC = () => {
  const [data, setData] = useState<TokenPaymentsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [search, setSearch] = useState('');
  const [searchApplied, setSearchApplied] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await paymentsService.getTokenPayments({
        page: page + 1, limit: rowsPerPage, status, startDate, endDate, search: searchApplied,
      }));
    } catch (e: any) {
      setError(e?.response?.data?.error || e.message || 'Erro ao carregar');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, status, startDate, endDate, searchApplied]);

  useEffect(() => { load(); }, [load]);

  const stats = data?.stats;
  const tiles = [
    { label: 'Pagamentos', value: String(stats?.total ?? 0) },
    { label: 'Pagos', value: String(stats?.paid ?? 0), color: 'success.main' },
    { label: 'Falharam', value: String(stats?.failed ?? 0), color: 'error.main' },
    { label: 'Valor pago', value: cve(stats?.totalPaid ?? 0) },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
        <CreditCard sx={{ color: 'primary.main' }} />
        <Typography variant="h5" fontWeight={700}>Pagamentos com Tokens</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Pagamentos feitos com cartão guardado (tokenização SISP). Os totais respeitam os filtros.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {tiles.map((t) => (
          <Grid key={t.label} size={{ xs: 6, md: 3 }}>
            <Card>
              <CardContent sx={{ py: 2 }}>
                <Typography variant="caption" color="text.secondary">{t.label}</Typography>
                <Typography variant="h5" fontWeight={700} sx={{ color: t.color }}>{t.value}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth size="small" placeholder="Referência, nº, cliente ou email"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { setPage(0); setSearchApplied(search.trim()); } }}
                InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4, md: 2 }}>
              <TextField fullWidth size="small" select label="Estado" value={status}
                onChange={(e) => { setPage(0); setStatus(e.target.value); }}>
                <MenuItem value="">Todos</MenuItem>
                {Object.entries(ESTADOS).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
              <TextField fullWidth size="small" type="date" label="De" value={startDate}
                onChange={(e) => { setPage(0); setStartDate(e.target.value); }} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
              <TextField fullWidth size="small" type="date" label="Até" value={endDate}
                onChange={(e) => { setPage(0); setEndDate(e.target.value); }} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <Button fullWidth variant="outlined" startIcon={<Refresh />} onClick={load}>Atualizar</Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Paper variant="outlined">
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Data</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Referência</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Cliente</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Cartão</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Entidade / Serviço</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Montante</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Estado</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(data?.data || []).length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      Sem pagamentos com token para estes filtros.
                    </TableCell>
                  </TableRow>
                )}
                {(data?.data || []).map((p: TokenPayment) => {
                  const est = ESTADOS[p.status] || { label: p.status, color: 'default' as const };
                  return (
                    <TableRow key={p.id} hover>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{dataCV(p.createdAt)}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{p.reference}</TableCell>
                      <TableCell>
                        <Typography variant="body2">{p.customerName || '—'}</Typography>
                        {p.customerEmail && <Typography variant="caption" color="text.secondary">{p.customerEmail}</Typography>}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {p.cardLast4 ? `${p.cardType || 'Cartão'} •••• ${p.cardLast4}` : '—'}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{p.entityName || '—'}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {[p.serviceLabel || TIPOS_TX[p.transactionCode || ''], p.serviceReference].filter(Boolean).join(' · ')}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>{cve(p.amount)}</TableCell>
                      <TableCell><Chip size="small" color={est.color} label={est.label} /></TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        )}
        <TablePagination
          component="div"
          count={data?.pagination.total || 0}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[20, 50, 100]}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          labelRowsPerPage="Por página"
        />
      </Paper>
    </Box>
  );
};

export default TokenPayments;
